/* File: AttendanceTopNav.jsx
 * Path: ananunadm/src/components/Attendance/AttendanceTopNav.jsx
 *
 * Shared 4-tab top nav (Attendance Dashboard / Reports / Manual Entry /
 * Day off), used by both components/hr/attendance/attendanceDashboard.jsx
 * and pages/Attendance/AttendanceReportsPage.jsx. Wraps its `children` in
 * the same Mantine <Tabs> so callers can render <Tabs.Panel> children that
 * need the shared Tabs context, exactly like the original single-file
 * tab bar did.
 *
 * When already on /hr/attendance and switching among its own remaining
 * tabs (attendance/manualEntry/dayOff), delegates to `onLocalTabChange`
 * (the existing same-page handleTabChange) so that behavior is unchanged.
 * Any other transition (going to/from Reports, or reaching
 * attendance/manualEntry/dayOff from the Reports page) is a cross-route
 * history.push.
 */
import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Tabs } from "@mantine/core";
import { IconUserCheck, IconTable, IconEdit, IconMoon, IconCalendarStats } from "@tabler/icons-react";
import { T } from "../../helpers/translator";
import { ResponsiveTabsList } from "../../helpers/ResponsiveTabsList";
import config from "../../config";
import classes from "../Tabs.module.css";

export function AttendanceTopNav({ lang, active, onLocalTabChange, children }) {
	const history = useHistory();
	const moduleColor =
		config.originalMenu?.find((item) => item.route === "hr")?.color || "#12b886";

	const handleChange = useCallback(
		(value) => {
			if (!value || value === active) return;
			if (value === "reports") {
				history.push("/hr/attendance/reports");
				return;
			}
			if (value === "calendar") {
				history.push("/hr/attendance/calendar");
				return;
			}
			if (onLocalTabChange) {
				onLocalTabChange(value);
				return;
			}
			history.push(value === "attendance" ? "/hr/attendance" : `/hr/attendance?tab=${value}`);
		},
		[active, history, onLocalTabChange]
	);

	return (
		<Tabs
			value={active}
			onTabChange={handleChange}
			variant="unstyled"
			classNames={classes}
			keepMounted={false}
			style={{ "--apit-tab-active-color": moduleColor }}
		>
			<ResponsiveTabsList
				value={active}
				onChange={handleChange}
				items={[
					{
						value: "attendance",
						label: T("attendanceDashboard", lang),
						icon: <IconUserCheck size={14} />
					},
					{
						value: "reports",
						label: T("attendanceReports", lang) || "Reports",
						icon: <IconTable size={14} />
					},
					{
						value: "calendar",
						label: T("attendanceCalendarTab", lang) || "Calendar",
						icon: <IconCalendarStats size={14} />
					},
					{
						value: "manualEntry",
						label: T("manualAttendanceEntry", lang) || "Manual entry",
						icon: <IconEdit size={14} />
					},
					{
						value: "dayOff",
						label: T("attendanceDayOffTab", lang) || "Day off",
						icon: <IconMoon size={14} />
					}
				]}
			/>
			{children}
		</Tabs>
	);
}

export default AttendanceTopNav;
